import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { AuthService, UserProfile } from '../../services/auth.service';
import { SidebarService } from '../../services/sidebar.service';
import { PermissionService } from '../../services/permission.service';
import { Observable, combineLatest, map } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';

interface NavItem {
  label: string;
  icon: string;
  route?: string;
  queryParams?: any;
  action?: () => void;
  permission?: string;
  /** Rótulo chico sobre el label (ej. "Inversión" sobre "BIP 40036206-0") — da contexto sin
   * agregar una fila/nivel de indentación aparte. */
  subtitle?: string;
  /** Ítems hijos, renderizados con un nivel de indentación bajo este (ej. Beneficiarios/Flujo
   * bajo el programa) — el sidebar solo soportaba Grupo→Ítems planos, esto agrega un único
   * nivel más para el caso de Programas sin rehacer el modelo a un árbol genérico. */
  children?: NavItem[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

import { ComunicadoService } from '../../services/comunicado.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  isCollapsed$: Observable<boolean>;
  isAuthenticated$: Observable<boolean>;
  userProfile$: Observable<UserProfile | null>;
  unreadCount$: Observable<number>;
  
  private permissionService = inject(PermissionService);
  private comunicadoService = inject(ComunicadoService);

  menuGroups: NavGroup[] = [
    {
      title: '',
      items: [
        { label: 'Sitio público', icon: 'visibility', route: '/' },
      ]
    },
    {
      title: 'Tu día',
      items: [
        { label: 'Conoce más', icon: 'info', route: '/admin/dashboard', permission: 'dashboard.read' },
        { label: 'Buzón de Mensajes', icon: 'email', route: '/admin/inbox', permission: 'dashboard.read' },
        { label: 'Mi Actividad', icon: 'history', route: '/admin/activity', permission: 'dashboard.read' },
      ]
    },
    {
      title: 'Contenido',
      items: [
        { label: 'Noticias', icon: 'newspaper', route: '/admin/news', permission: 'news.read' },
        { label: 'Eventos', icon: 'event', route: '/admin/events', permission: 'events.read' },
        { label: 'Carrusel', icon: 'view_carousel', route: '/admin/carousel', permission: 'carousel.read' },
        { label: 'Banners', icon: 'flag', route: '/admin/banner', permission: 'banner.read' },
        { label: 'Servicios', icon: 'business_center', route: '/admin/service', permission: 'services.read' },
        { label: 'Nosotros', icon: 'groups', route: '/admin/about', permission: 'about.read' },
        { label: 'Contacto/RRSS', icon: 'contact_page', route: '/admin/contact', permission: 'contact.read' },
        { label: 'Galerías', icon: 'photo_library', route: '/admin/galleries', permission: 'gallery.read' },
        { label: 'Multimedia', icon: 'collections', route: '/admin/gallery', permission: 'gallery.read' },
        { label: 'Aplicaciones', icon: 'apps', route: '/admin/external-apps', permission: 'external_apps.read' },
      ]
    },
    {
      title: 'Transparencia',
      items: [
        { label: 'Transparencia', icon: 'gavel', route: '/admin/transparency', permission: 'transparency.read' },
      ]
    },
    {
      title: 'Herramientas',
      items: [
        { label: 'Comunicados', icon: 'campaign', route: '/admin/comunicados', permission: 'comunicados.read' },
        { label: 'Documentos', icon: 'auto_stories', route: '/admin/document-center', permission: 'document_center.read' },
        { label: 'Recursos Humanos', icon: 'badge', route: '/admin/rrhh', permission: 'rrhh.read' },
      ]
    },
    {
      title: 'Programas',
      items: [
        {
          label: 'BIP 40036206-0',
          subtitle: 'Inversión',
          icon: 'query_stats',
          route: '/admin/invierte-dashboard/postulaciones',
          permission: 'invierte_dashboard.view',
          children: [
            { label: 'Beneficiarios', icon: 'emoji_events', route: '/admin/invierte-dashboard/beneficiarios', permission: 'invierte_dashboard.view' },
            { label: 'Flujo del programa', icon: 'account_tree', route: '/admin/invierte-dashboard/flujo', permission: 'invierte_dashboard.view' },
          ],
        },
      ]
    },
    {
      title: 'Inteligencia Artificial',
      items: [
        { label: 'Chat Libre', icon: 'chat', route: '/admin/chat', permission: 'chat.read' },
        { label: 'Workstation', icon: 'terminal', route: '/admin/workstation/home', permission: 'chat.read' },
        { label: 'Mis Agentes', icon: 'smart_toy', route: '/admin/mis-agentes', permission: 'agent.create.own' },
      ]
    },
    {
      title: 'Administración IA',
      items: [
        { label: 'Agentes IA', icon: 'smart_toy', route: '/admin/ai/agents', permission: 'chat.quota.manage' },
        { label: 'Bases de Conocimiento', icon: 'storage', route: '/admin/ai/kbases', permission: 'chat.quota.manage' },
        { label: 'Servidores MCP', icon: 'dns', route: '/admin/ai/mcp-servers', permission: 'chat.quota.manage' },
        { label: 'Cuotas de Chat', icon: 'admin_panel_settings', route: '/admin/chat-quotas', permission: 'chat.quota.manage' },
        { label: 'Métricas de Chat', icon: 'bar_chart', route: '/admin/chat-metrics', permission: 'chat.quota.manage' },
      ]
    },
    {
      title: 'Usuarios',
      items: [
        { label: 'Usuarios', icon: 'manage_accounts', route: '/admin/users', permission: 'security.read' },
        { label: 'Grupos', icon: 'groups_2', route: '/admin/groups', permission: 'security.read' },
        { label: 'Roles', icon: 'admin_panel_settings', route: '/admin/roles', permission: 'security.read' },
        { label: 'Permisos', icon: 'security', route: '/admin/permissions', permission: 'security.read' },
      ]
    },
  ];

  collapsedGroups: { [key: string]: boolean } = {};
  filteredGroups$: Observable<NavGroup[]>;

  constructor(
    private sidebarService: SidebarService,
    private authService: AuthService,
    private router: Router
  ) {
    this.isCollapsed$ = this.sidebarService.isCollapsed$;
    this.isAuthenticated$ = this.authService.isAuthenticated$;
    this.userProfile$ = this.authService.userProfile$;

    this.unreadCount$ = this.comunicadoService.announcements$.pipe(
      map(list => list.filter(ann => !ann.read).length)
    );

    this.filteredGroups$ = combineLatest([
      this.userProfile$,
      this.permissionService.userPermissions$
    ]).pipe(
      map(([profile]) => {
        return this.menuGroups.map(group => ({
          ...group,
          items: group.items
            .filter(item => this.hasItemAccess(item, profile))
            .map(item => item.children
              ? { ...item, children: item.children.filter(child => this.hasItemAccess(child, profile)) }
              : item)
        })).filter(group => group.items.length > 0);
      })
    );
  }

  ngOnInit(): void { }

  private hasItemAccess(item: NavItem, profile: UserProfile | null): boolean {
    if (!item.permission) return true;
    if (!profile) return false;

    // Bypass completo para administradores globales
    const isGlobalAdmin = profile.roles.includes('admin');
    return isGlobalAdmin || this.permissionService.hasPermission(item.permission);
  }

  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }

  toggleGroup(groupTitle: string): void {
    if (!groupTitle) return;
    this.collapsedGroups[groupTitle] = !this.collapsedGroups[groupTitle];
  }

  isGroupCollapsed(groupTitle: string): boolean {
    return !!this.collapsedGroups[groupTitle];
  }

  navigateToSite(): void {
    this.router.navigate(['/']);
  }

  logout(): void {
    this.authService.logout();
  }

  getUserInitials(profile: UserProfile | null): string {
    if (!profile?.displayName) return '??';
    const parts = profile.displayName.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return profile.displayName.substring(0, 2).toUpperCase();
  }

  isActive(route?: string, queryParams?: any): boolean {
    if (!route) return false;
    const urlTree = this.router.parseUrl(this.router.url);
    const primarySegment = urlTree.root.children['primary'];
    const currentUrlBase = primarySegment ? '/' + primarySegment.segments.map(s => s.path).join('/') : '';

    // /admin/workstation/:id should highlight the Workstation menu item
    if (route === '/admin/workstation') {
      return currentUrlBase.startsWith('/admin/workstation');
    }

    // /admin/mis-agentes/nuevo, /personalizar and /:agentId should all highlight this item too
    if (route === '/admin/mis-agentes') {
      return currentUrlBase.startsWith('/admin/mis-agentes');
    }

    const isBaseActive = currentUrlBase === route;
    if (!isBaseActive) return false;

    const currentParams = urlTree.queryParams;
    if (queryParams) {
      return Object.keys(queryParams).every(key => String(currentParams[key]) === String(queryParams[key]));
    } else {
      const specificKeys = ['workstation', 'chatView', 'chatTab'];
      return !specificKeys.some(key => currentParams[key] !== undefined);
    }
  }
}
